import * as React from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import CButton from "../../../basecomp/components/CButton";
import CInput from "../../../basecomp/components/CInput";
import CText from "../../../basecomp/components/CText";
import CCheckButton from "../../../basecomp/components/CCheckButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Autocomplete from "@mui/material/Autocomplete";
import { searchMaterials } from "../model/MatRequestModel";

const TABLE_FONT = "'Inter', 'Roboto', sans-serif";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: TABLE_FONT,
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#fcf8f8",
    color: "#0f172a",
    fontSize: 15,
    fontFamily: TABLE_FONT,
    borderBottom: "2px solid #cbd5e1",
    borderRight: "1px solid #e2e8f0",
    fontWeight: 700,
    letterSpacing: "0.01em",
    padding: "10px 16px",
    "&:last-child": {
      borderRight: "none",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 10,
    fontFamily: TABLE_FONT,
    borderBottom: "1px solid #dbe3ef",
    borderRight: "1px solid #eef1f6",
    color: "#334155",
    padding: "6px 12px",
    "&:last-child": {
      borderRight: "none",
    },
  },
}));

const DEFAULT_PAGE_SIZE = 10;

const STATUS_STYLES = {
  "In Progress": {
    color: "#1d4ed8",
    backgroundColor: "#eff6ff",
    borderColor: "#bfdbfe",
  },
  Pending: {
    color: "#b45309",
    backgroundColor: "#fffbeb",
    borderColor: "#fde68a",
  },
  "On Hold": {
    color: "#ed3ad8",
    backgroundColor: "#f5f3ff",
    borderColor: "#ddd6fe",
  },
  "Validation Failed": {
    color: "#c2410c",
    backgroundColor: "#fff7ed",
    borderColor: "#fed7aa",
  },
  Approved: {
    color: "#15803d",
    backgroundColor: "#f0fdf4",
    borderColor: "#bbf7d0",
  },
  Rejected: {
    color: "#b91c1c",
    backgroundColor: "#fef2f2",
    borderColor: "#fecaca",
  },
  // fallback style so an unknown/mismatched status string isn't unstyled
  __default: {
    color: "#475569",
    backgroundColor: "#f1f5f9",
    borderColor: "#e2e8f0",
  },
};

// ── Status matching ──────────────────────────────────────────────────────
// Normalizes casing/spacing ("ApproVed", "on-hold", "in_progress") and falls
// back to keyword matching so differently-worded statuses still get a color.
const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const STATUS_STYLE_EXACT_LOOKUP = Object.keys(STATUS_STYLES).reduce(
  (acc, key) => {
    if (key === "__default") return acc;
    acc[normalizeStatusKey(key)] = key;
    return acc;
  },
  {},
);

// Ordered keyword fallbacks — first match wins.
const STATUS_KEYWORD_FALLBACKS = [
  { keyword: "validationfail", key: "Validation Failed" },
  { keyword: "approv", key: "Approved" },
  { keyword: "reject", key: "Rejected" },
  { keyword: "hold", key: "On Hold" },
  { keyword: "progress", key: "In Progress" },
  { keyword: "pending", key: "Pending" },
];

const getStatusStyle = (rawStatus) => {
  if (!rawStatus || rawStatus === "-") return STATUS_STYLES.__default;

  const normalized = normalizeStatusKey(rawStatus);

  const exactKey = STATUS_STYLE_EXACT_LOOKUP[normalized];
  if (exactKey) return STATUS_STYLES[exactKey];

  const fallback = STATUS_KEYWORD_FALLBACKS.find(({ keyword }) =>
    normalized.includes(keyword),
  );
  if (fallback) return STATUS_STYLES[fallback.key];

  return STATUS_STYLES.__default;
};

// ── Material search (DB) ─────────────────────────────────────────────────
const MIN_SEARCH_CHARS = 3;

const useMaterialSearch = (term, delay = 400) => {
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_SEARCH_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false; // ignore responses from outdated searches

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchMaterials(q);
        if (cancelled) return;
        // handles both an axios response and an already-unwrapped array
        const data = res?.data ?? res;
        setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, delay]);

  return { results, loading };
};
// ─────────────────────────────────────────────────────────────────────────

const TEXT_STATUS_STYLES = {
  fail: { color: STATUS_STYLES.Rejected.color, Icon: CancelIcon },
  pass: { color: STATUS_STYLES.Approved.color, Icon: CheckCircleIcon },
};

// Column width configuration
const COLUMN_WIDTHS = {
  Date: "100px",
  "Plant Code": "80px",
  Status: "90px",
  "Request Status ": "140px",
  "Material No": "150px",
  "Rejected Comments": "700px",
  Reason: "400px",
  Description: "400px",
};
// Accepts seconds, milliseconds, or an already formatted string → DD-MM-YYYY
const formatRequestDate = (raw) => {
  if (raw === null || raw === undefined || raw === "" || raw === "NaN") {
    return "—";
  }
  const n = Number(raw);
  if (Number.isNaN(n)) return String(raw); // already formatted, leave as is

  const ms = n < 1e11 ? n * 1000 : n; // below 1e11 means seconds
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
};
const MCTTable = ({
  cHeaderData,
  cRowData,
  cActualData,
  cTooltipText,
  cIsPagination,
  cSmallSize,
  cCustomRows,
  showSearch = true,
  cIsLoading = false,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = React.useState("");
  const { results: materialResults, loading: searchLoading } =
    useMaterialSearch(searchTerm);

  const exactMatch = materialResults.find(
    (m) =>
      String(m.material_number).toLowerCase() ===
      searchTerm.trim().toLowerCase(),
  );

  // Rows that come from the parent (used to tell DB-only rows apart)
  const tableIds = React.useMemo(
    () => new Set((cActualData || []).map((r) => String(r.id))),
    [cActualData],
  );

  // Local filter over the rows the parent passed in
  const filteredData = React.useMemo(() => {
    const filtered = [...(cActualData || [])];

    if (searchTerm) {
      const trimmedSearch = searchTerm.trim().toLowerCase();
      return filtered.filter((row) =>
        cRowData.some((rowData) => {
          const cellValue =
            typeof rowData.value === "function"
              ? rowData.value(row)
              : row[rowData.value];
          const cellValueString =
            cellValue != null ? cellValue.toString().toLowerCase() : "";
          return cellValueString.includes(trimmedSearch);
        }),
      );
    }

    return filtered;
  }, [searchTerm, cActualData, cRowData]);

  // Local rows + DB matches that are not already in the table
  // (e.g. Approved / Rejected rows the parent hides)
 const displayData = React.useMemo(() => {
  const q = searchTerm.trim();
  if (q.length < MIN_SEARCH_CHARS || materialResults.length === 0) {
    return filteredData;
  }
  const extraRows = materialResults
    .filter((r) => !tableIds.has(String(r.id)))
    .map((r) => ({ ...r, request_date: formatRequestDate(r.request_date) }));
  return [...filteredData, ...extraRows];
}, [filteredData, materialResults, tableIds, searchTerm]);

  // Keep pagination valid if the data shrinks
  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(displayData.length / rowsPerPage) - 1,
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [displayData.length, rowsPerPage, page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const getColumnWidth = (headerText) => {
    const text = typeof headerText === "object" ? headerText.text : headerText;
    return COLUMN_WIDTHS[text?.trim()] || "175px";
  };

  const getCellBackgroundColor = (rowData, actualRow) => {
    if (rowData.statusType === "bg") {
      if (typeof rowData.status === "function") {
        const statusValue = rowData.status(actualRow);
        switch (statusValue) {
          case 0:
            return "#E0E0E0";
          case 1:
            return "#00CC00";
          case 2:
            return "#FF7F7F";
          case 3:
            return "#ffe0b2";
          default:
            return "#FFFFFF";
        }
      }
    }
    return "inherit";
  };

  const handleCellData = (rowData, actualRow, rowIndex) => {
    let cellContent;
    let rowDataType = rowData.type;
    if (rowDataType === "M") {
      rowDataType = rowData.actualType(actualRow);
    }
    if (rowDataType === "T") {
      if (rowData.value === "status") {
        const statusValue = actualRow[rowData.value] || "-";
        const chipStyle = getStatusStyle(statusValue);
        cellContent = (
          <Chip
            label={statusValue}
            size="small"
            variant="outlined"
            icon={
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: chipStyle.color,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
            }
            sx={{
              height: 24,
              borderRadius: "999px",
              fontSize: 12,
              fontFamily: "inherit",
              fontWeight: 700,
              letterSpacing: "0.01em",
              px: 0.25,
              border: `1px solid ${chipStyle.borderColor}`,
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              minWidth: 76,
              justifyContent: "flex-start",
              transition: "box-shadow 150ms ease, transform 150ms ease",
              "&:hover": {
                boxShadow: "0 2px 6px rgba(15, 23, 42, 0.10)",
              },
              "& .MuiChip-icon": {
                ml: 1,
                mr: 0,
                width: 6,
                height: 6,
              },
              "& .MuiChip-label": {
                px: 0.75,
                pl: 0.75,
              },
              ...chipStyle,
            }}
          />
        );
      } else if (rowData.tooltipText) {
        cellContent = (
          <Tooltip
            title={
              rowData.tooltipText && (
                <Typography
                  style={{ whiteSpace: "pre-line" }}
                  sx={{ fontSize: "1em" }}
                >
                  {rowData.tooltipText(actualRow)}
                </Typography>
              )
            }
          >
            <span>
              <CText
                cText={actualRow[rowData.value]}
                cIsJson={rowData.isJson}
              />
            </span>
          </Tooltip>
        );
      } else {
        cellContent = (
          <CText cText={actualRow[rowData.value]} cIsJson={rowData.isJson} />
        );
      }
    } else if (rowDataType === "B") {
      cellContent =
        rowData.value === "Start" ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="small"
              disabled={rowData.isDisable(actualRow)}
              onClick={() => rowData.actionV(actualRow)}
              sx={{
                background: "linear-gradient(135deg, #042560 0%, #1e293b 100%)",
                color: "#fff",
                fontFamily: "inherit",
                fontWeight: 500,
                fontSize: 12,
                lineHeight: 1,
                borderRadius: "6px",
                minWidth: "68px",
                minHeight: 0,
                height: 28,
                px: 1.5,
                py: 0,
                textTransform: "none",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 14px rgba(4, 37, 96, 0.18)",
                },
                "&:disabled": {
                  background: "#cbd5e1",
                  color: "#94a3b8",
                },
              }}
            >
              Start
            </Button>
          </Box>
        ) : (
          <CButton
            cText={rowData.value}
            cOnClick={() => rowData.actionV(actualRow)}
            cDisabled={rowData.isDisable(actualRow)}
          />
        );
    } else if (rowDataType === "I" || rowDataType === "IC") {
      let isShow = true;
      if (rowData.status !== undefined && actualRow[rowData.status] === 0) {
        isShow = false;
      }
      if (isShow) {
        const tooltipText =
          rowDataType === "IC"
            ? rowData.tooltipText(actualRow)
            : rowData.tooltipText;
        cellContent = (
          <CButton
            cSrc={
              rowDataType === "IC" ? rowData.value(actualRow) : rowData.value
            }
            cTooltipText={tooltipText}
            aria-label={tooltipText}
            cOnClick={() =>
              rowData.actionV(actualRow, page * rowsPerPage + rowIndex)
            }
          />
        );
      }
    } else if (rowDataType === "E") {
      cellContent = (
        <CInput
          cValue={actualRow[rowData.value]}
          cOnChange={rowData.actionV}
          cType={rowData.eType}
        />
      );
    } else if (rowDataType === "C") {
      cellContent = (
        <CCheckButton
          cChecked={actualRow.status === 1}
          cDisabled={true}
          cTooltipText={actualRow.status === 1 ? "Checked" : "Unchecked"}
        />
      );
    } else if (rowDataType === "S") {
      cellContent = (rowIndex + 1).toString() + ".";
    } else if (rowDataType === "L") {
      const tooltipContent = rowData.tooltipText
        ? typeof rowData.tooltipText === "function"
          ? rowData.tooltipText(actualRow)
          : rowData.tooltipText
        : "";

      cellContent = (
        <Tooltip
          title={
            tooltipContent && (
              <Typography
                style={{ whiteSpace: "pre-line" }}
                sx={{ fontSize: "1em" }}
              >
                {tooltipContent}
              </Typography>
            )
          }
        >
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              rowData.actionV(actualRow);
            }}
            style={{
              color: "#1d4ed8",
              textDecoration: "underline",
              cursor: "pointer",
              whiteSpace: "nowrap",
              border: 0,
              background: "transparent",
              padding: 0,
              font: "inherit",
              fontFamily: "inherit",
            }}
          >
            {actualRow[rowData.value]}
          </button>
        </Tooltip>
      );
    } else if (rowDataType === "F") {
      const value =
        typeof rowData.value === "function"
          ? rowData.value(actualRow)
          : rowData.value;
      cellContent = <CText cText={value} />;
    }
    return cellContent;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        overflow: "hidden",
        border: "1px solid #cbd5e1",
        borderRadius: "8px",
        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
        background: "#ffffff",
        fontFamily: (theme) => theme.typography.fontFamily,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #cbd5e1",
          background:
            "linear-gradient(180deg, rgba(248,250,252,0.95), rgba(255,255,255,0.95))",
        }}
      >
        {showSearch && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              freeSolo
              size="small"
              options={materialResults}
              loading={searchLoading}
              inputValue={searchTerm}
              onInputChange={(_, value, reason) => {
                if (
                  reason === "input" ||
                  reason === "clear" ||
                  reason === "reset"
                ) {
                  handleSearchChange({ target: { value } });
                }
              }}
              filterOptions={(x) => x} // server already filtered
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : String(opt.material_number)
              }
              noOptionsText={
                searchTerm.trim().length < MIN_SEARCH_CHARS
                  ? `Type at least ${MIN_SEARCH_CHARS} characters`
                  : "No matching material in database"
              }
              sx={{ width: { xs: "100%", sm: 320 } }}
              renderOption={(props, opt) => {
                const { key, ...rest } = props;
                const chip = getStatusStyle(opt.status);
                return (
                  <li key={opt.id ?? key} {...rest}>
                    <Box sx={{ width: "100%", minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {opt.material_number}
                          <Typography
                            component="span"
                            sx={{ fontSize: 11, color: "#64748b", ml: 1 }}
                          >
                            Plant {opt.plant_id}
                          </Typography>
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            px: 1,
                            borderRadius: "999px",
                            color: chip.color,
                            backgroundColor: chip.backgroundColor,
                            border: `1px solid ${chip.borderColor}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {opt.status}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#64748b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {opt.description}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search requests / check material"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#64748b", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    fontFamily: "inherit",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      fontFamily: "inherit",
                      fontSize: 13,
                      "& fieldset": { borderColor: "#dbe3ef" },
                      "&:hover fieldset": { borderColor: "#042560" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#042560",
                        borderWidth: "1px",
                      },
                    },
                  }}
                />
              )}
            />

            {exactMatch && (
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#b45309",
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.75,
                }}
              >
                ⚠️ Material {exactMatch.material_number} already exists (
                {exactMatch.status}, Plant {exactMatch.plant_id})
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <TableContainer
        sx={{ maxHeight: "calc(100vh - 290px)", overflowX: "auto" }}
      >
        <Table
          aria-label={cTooltipText}
          size={cSmallSize === true ? "small" : "inherit"}
          stickyHeader
          sx={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <TableHead>
            <TableRow>
              {cHeaderData.map((headData, headIndex) => {
                return headData.tooltipText ? (
                  <Tooltip
                    key={headIndex}
                    title={
                      <Typography
                        style={{ whiteSpace: "pre-line" }}
                        sx={{ fontSize: "1em" }}
                      >
                        {headData.tooltipText}
                      </Typography>
                    }
                  >
                    <StyledTableCell
                      key={headIndex}
                      keyIndex={headIndex}
                      sx={{
                        width: getColumnWidth(headData),
                        minWidth: getColumnWidth(headData),
                        maxWidth: getColumnWidth(headData),
                        whiteSpace:
                          (typeof headData === "object"
                            ? headData.text
                            : headData) === "Request Status "
                            ? "nowrap"
                            : "normal",
                        wordWrap: "break-word",
                      }}
                    >
                      {headData.text}
                    </StyledTableCell>
                  </Tooltip>
                ) : (
                  <StyledTableCell
                    key={headIndex}
                    keyIndex={headIndex}
                    sx={{
                      width: getColumnWidth(headData),
                      minWidth: getColumnWidth(headData),
                      maxWidth: getColumnWidth(headData),
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                  >
                    {headData}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {cIsLoading ? (
              <TableRow>
                <StyledTableCell
                  colSpan={cHeaderData.length}
                  sx={{ textAlign: "center", py: 6 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      color: "#64748b",
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: "#042560" }} />
                    <span>Loading requests…</span>
                  </Box>
                </StyledTableCell>
              </TableRow>
            ) : displayData.length > 0 ? (
              displayData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((actualRow, rowIndex) => {
                  // true for rows that came only from the DB search
                  const isDbOnlyRow = !tableIds.has(String(actualRow.id));

                  return (
                    <TableRow
                      key={actualRow.id ?? rowIndex}
                      sx={{
                        transition:
                          "background-color 160ms ease, transform 160ms ease",
                        backgroundColor: isDbOnlyRow ? "#fffbeb" : undefined,
                        "&:nth-of-type(even)": {
                          backgroundColor: isDbOnlyRow ? "#fffbeb" : "#fbfdff",
                        },
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      {cRowData.map((rowData, cellIndex) => {
                        const headerText = cHeaderData[cellIndex];
                        const isFailStatus =
                          rowData.status !== undefined &&
                          rowData.statusType === "text" &&
                          actualRow[rowData.status] === 0;
                        const textStatusStyle =
                          rowData.status === undefined ||
                          rowData.statusType !== "text"
                            ? null
                            : isFailStatus
                              ? TEXT_STATUS_STYLES.fail
                              : TEXT_STATUS_STYLES.pass;

                        return (
                          <StyledTableCell
                            component="th"
                            scope="row"
                            key={cellIndex}
                            sx={{
                              color: textStatusStyle
                                ? textStatusStyle.color
                                : "inherit",
                              backgroundColor: getCellBackgroundColor(
                                rowData,
                                actualRow,
                              ),
                              textAlign: "left",
                              whiteSpace:
                                headerText === "Rejected Comments"
                                  ? "normal"
                                  : "nowrap",
                              wordBreak:
                                headerText === "Rejected Comments"
                                  ? "break-word"
                                  : "normal",
                              width: getColumnWidth(headerText),
                              minWidth: getColumnWidth(headerText),
                              maxWidth: getColumnWidth(headerText),
                              overflow:
                                headerText === "Rejected Comments"
                                  ? "visible"
                                  : "hidden",
                              textOverflow:
                                headerText === "Rejected Comments"
                                  ? "clip"
                                  : "ellipsis",
                            }}
                          >
                            {textStatusStyle ? (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <textStatusStyle.Icon
                                  sx={{
                                    fontSize: 15,
                                    color: textStatusStyle.color,
                                  }}
                                />
                                {handleCellData(rowData, actualRow, rowIndex)}
                              </Box>
                            ) : (
                              handleCellData(rowData, actualRow, rowIndex)
                            )}
                          </StyledTableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
            ) : (
              <TableRow>
                <StyledTableCell
                  colSpan={cHeaderData.length}
                  sx={{ textAlign: "center", py: 6, color: "#64748b" }}
                >
                  {searchTerm
                    ? `No results for "${searchTerm}"`
                    : "No data found"}
                </StyledTableCell>
              </TableRow>
            )}
            {cCustomRows &&
              Object.keys(cCustomRows).map((key, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor:
                      index === Object.keys(cCustomRows).length - 1
                        ? "#f0f0f0"
                        : "transparent",
                  }}
                >
                  <StyledTableCell colSpan={cHeaderData.length - 2} />
                  <StyledTableCell align="right">
                    <b>{key}:</b>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <span style={{ marginLeft: "2.5em" }}>
                      {cCustomRows[key]}
                    </span>
                  </StyledTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {cIsPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={displayData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid #cbd5e1",
            color: "#475569",
            fontFamily: "inherit",
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              { margin: 0, fontFamily: "inherit", fontSize: 13 },
            ".MuiTablePagination-select": {
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              color: "#0f172a",
              borderRadius: "6px",
              border: "1px solid #dbe3ef",
              backgroundColor: "#ffffff",
              padding: "4px 24px 4px 10px !important",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              transition: "border-color 150ms ease, box-shadow 150ms ease",
              "&:hover": {
                borderColor: "#042560",
              },
              "&:focus": {
                borderColor: "#042560",
                boxShadow: "0 0 0 3px rgba(4, 37, 96, 0.12)",
              },
            },
            ".MuiTablePagination-select.Mui-disabled": {
              backgroundColor: "#f8fafc",
              borderColor: "#e8edf5",
              color: "#94a3b8",
            },
            ".MuiTablePagination-selectIcon": {
              color: "#64748b",
              right: 4,
            },
            ".MuiTablePagination-actions button": {
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            },
          }}
          SelectProps={{
            MenuProps: {
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              marginThreshold: 8,
              PaperProps: {
                sx: {
                  borderRadius: "8px",
                  border: "1px solid #dbe3ef",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                  mt: 0.5,
                  "& .MuiMenuItem-root": {
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#334155",
                    minHeight: 0,
                    py: 0.75,
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#eff6ff",
                      color: "#042560",
                      fontWeight: 700,
                      "&:hover": {
                        backgroundColor: "#eff6ff",
                      },
                    },
                  },
                },
              },
            },
          }}
        />
      )}
    </Paper>
  );
};

export default MCTTable;